import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Alert, Box, Typography } from '@mui/material';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';

const VerifyMembershipEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hash = searchParams.get('hash');
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [errorMessage, setErrorMessage] = useState('');

  const { send } = useRequest({
    url: `/api/v1/memberships/verify-email/${hash || 'invalid'}`,
    method: ApiMethodsEnum.Patch,
  });

  const hasAttempted = useRef(false);

  useEffect(() => {
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    const verifyEmail = async () => {
      if (!hash) {
        setVerificationStatus('error');
        setErrorMessage('Invalid verification link');
        return;
      }

      const response = await send();
      if (response) {
        setVerificationStatus('success');
        setTimeout(() => {
          navigate('/profile/company-memberships');
        }, 3000);
      } else {
        setVerificationStatus('error');
        setErrorMessage('Verification failed. Please try again.');
      }
    };

    verifyEmail();
  }, [hash, send, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
      }}
    >
      {verificationStatus === 'loading' && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Verifying your work email...
          </Typography>
        </>
      )}

      {verificationStatus === 'success' && (
        <Alert severity="success" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Email Verified Successfully!
          </Typography>
          <Typography>
            Your work email has been verified. Your membership is now pending admin approval.
            Redirecting to your company memberships page...
          </Typography>
        </Alert>
      )}

      {verificationStatus === 'error' && (
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Verification Failed
          </Typography>
          <Typography>{errorMessage}</Typography>
          <Typography sx={{ mt: 2 }}>
            <a href="/profile/company-memberships">Go to Company Memberships</a>
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default VerifyMembershipEmail;
